import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { manchiApi } from "@/lib/manchiApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, RefreshCw, Sparkles, PenLine, Copy, Linkedin, Save, ExternalLink,
  Wand2, Newspaper, Flame, CalendarDays, Link2,
} from "lucide-react";
import { toast } from "sonner";

const LENGTHS = ["Short", "Medium", "Long", "Essay", "Whitepaper", "Custom"];

export default function ManchiCompose() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loadingNews, setLoadingNews] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [streak, setStreak] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  const [tone, setTone] = useState("");
  const [language, setLanguage] = useState("");
  const [length, setLength] = useState("Medium");
  const [customWords, setCustomWords] = useState(600);
  const [extra, setExtra] = useState("");

  const [linkUrl, setLinkUrl] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [linkSummary, setLinkSummary] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [article, setArticle] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadNews = useCallback(async () => {
    setLoadingNews(true);
    try {
      const { data } = await manchiApi.get("/news");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch news");
      setItems([]);
    } finally {
      setLoadingNews(false);
    }
  }, []);

  useEffect(() => {
    manchiApi.get("/config").then(({ data }) => {
      setCfg(data);
      setTone(data.tone || "");
      setLanguage(data.language || "English");
      setLength(data.article_length || "Medium");
      setExtra(data.refine_prompt || "");
    });
    manchiApi.get("/streak").then(({ data }) => setStreak(data)).catch(() => {});
    loadNews();
  }, [loadNews]);

  const scheduleWeek = async () => {
    setScheduling(true);
    try {
      const { data } = await manchiApi.post("/schedule-week");
      toast.success(`Building ${data.count} themed drafts ✨ — they'll appear in Drafts over the next minute or two`);
      navigate("/manchi/studio/drafts", { state: { scheduling: true } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the week");
    } finally {
      setScheduling(false);
    }
  };

  const toggle = (it) => {
    const key = it.url || it.title;
    setSelected((s) => { const c = { ...s }; if (c[key]) delete c[key]; else c[key] = it; return c; });
  };

  const generate = async () => {
    setGenerating(true);
    setArticle(null);
    try {
      const chosen = Object.values(selected);
      const { data } = await manchiApi.post("/article/generate", {
        selected: chosen.map((it) => ({ title: it.title, snippet: it.snippet, url: it.url, source: it.source })),
        extra_prompt: extra,
        tone, language, length,
        custom_words: length === "Custom" ? Number(customWords) : undefined,
      });
      setArticle(data);
      if (data.status === "generating") {
        toast.success(`Writing your ${length}… this one takes a little longer ✨`);
        let ticks = 0;
        const poll = setInterval(async () => {
          ticks += 1;
          try {
            const { data: fresh } = await manchiApi.get(`/articles/${data.id}`);
            if (fresh.status !== "generating") {
              clearInterval(poll);
              setArticle(fresh);
              setGenerating(false);
              toast.success("Draft ready ✨ — review & tweak below");
            }
          } catch { /* keep polling */ }
          if (ticks >= 40) { clearInterval(poll); setGenerating(false); toast.message("Still writing — check Drafts in a minute"); }
        }, 5000);
        return;
      }
      toast.success("Draft ready ✨ — review & tweak below");
      setGenerating(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
      setGenerating(false);
    }
  };

  const fullText = article
    ? `${article.title}\n\n${article.body}\n\n${(article.hashtags || []).join(" ")}`
    : "";

  const copyAndOpen = async (open) => {
    try { await navigator.clipboard.writeText(fullText); toast.success("Copied to clipboard 📋"); }
    catch { toast.message("Select & copy the text manually"); }
    if (open) window.open("https://www.linkedin.com/feed/?shareActive=true", "_blank", "noopener");
  };

  const saveEdits = async () => {
    if (!article) return;
    setSaving(true);
    try {
      await manchiApi.patch(`/articles/${article.id}`, {
        title: article.title, body: article.body, hashtags: article.hashtags,
      });
      toast.success("Saved to Drafts");
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  const selCount = Object.keys(selected).length;

  const summarizeLink = async () => {
    if (!linkUrl.trim()) return;
    setSummarizing(true);
    setLinkSummary(null);
    try {
      const { data } = await manchiApi.post("/summarize-link", { url: linkUrl.trim() });
      setLinkSummary(data);
      toast.success("Summarised ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't summarise that link");
    } finally {
      setSummarizing(false);
    }
  };

  const useLinkAsSource = () => {
    if (!linkSummary) return;
    const item = { title: linkSummary.title, snippet: linkSummary.summary.slice(0, 400), url: linkSummary.url, source: "saved link" };
    setSelected((s) => ({ ...s, [linkSummary.url]: item }));
    if (linkSummary.angle) setExtra((e) => (e ? e + " " : "") + `Angle: ${linkSummary.angle}`);
    toast.success("Added as a source — now hit Generate");
  };

  return (
    <div className="space-y-6" data-testid="manchi-compose">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-rose-400 flex items-center gap-2">
            <PenLine className="w-4 h-4" /> today&apos;s post
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-rose-900 mt-1">Compose your daily article.</h1>
          <p className="text-rose-500 text-sm mt-2">
            Pick a few stories, tune the voice, and let ManchiGirl draft a LinkedIn-ready post.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {streak && (
            <div data-testid="manchi-streak-pill"
              className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-100 to-pink-100 border border-pink-200 px-3.5 py-2">
              <Flame className={`w-5 h-5 ${streak.current_streak > 0 ? "text-orange-500" : "text-rose-300"}`} fill={streak.current_streak > 0 ? "#fb923c" : "none"} />
              <div className="leading-tight">
                <div className="text-rose-900 font-black text-sm" data-testid="manchi-streak-count">
                  {streak.current_streak}-day streak
                </div>
                <div className="text-[10px] text-rose-400">
                  {streak.posted_today ? "posted today ✓" : streak.current_streak > 0 ? "post today to keep it!" : `${streak.total_posted} posts total`}
                </div>
              </div>
            </div>
          )}
          <Button onClick={scheduleWeek} disabled={scheduling} data-testid="manchi-schedule-week-btn"
            className="rounded-2xl bg-white border border-pink-300 text-rose-600 hover:bg-pink-50 font-semibold shadow-sm">
            {scheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalendarDays className="w-4 h-4 mr-2" />}
            Schedule this week
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
        {/* News picker */}
        <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-bold text-rose-900 flex items-center gap-2"><Newspaper className="w-4 h-4 text-pink-500" /> Worldwide headlines</div>
            <button onClick={loadNews} data-testid="manchi-refresh-news" className="text-xs font-semibold text-rose-500 hover:text-rose-700 flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingNews ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          {loadingNews ? (
            <div className="text-center py-12 text-rose-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-pink-200 p-8 text-center text-rose-400 text-sm">
              No headlines right now. Pick more <Link to="/manchi/studio/sources" className="text-pink-600 underline">sources</Link> or
              <Link to="/manchi/studio/globe" className="text-pink-600 underline"> countries</Link>. You can still generate from your topic below.
            </div>
          ) : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {items.map((it, i) => {
                const key = it.url || it.title;
                const on = !!selected[key];
                return (
                  <button
                    key={i}
                    onClick={() => toggle(it)}
                    data-testid={`manchi-news-item-${i}`}
                    className={`w-full text-left rounded-2xl border p-3.5 transition flex gap-3 ${
                      on ? "border-pink-400 bg-pink-50" : "border-pink-100 hover:border-pink-300 bg-white"
                    }`}
                  >
                    <span className={`mt-0.5 w-5 h-5 rounded-md flex-shrink-0 border flex items-center justify-center text-white ${on ? "bg-pink-500 border-pink-500" : "border-pink-300"}`}>
                      {on && <Sparkles className="w-3 h-3" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[10px] uppercase tracking-widest text-rose-400">{it.source}</span>
                      <span className="block font-semibold text-rose-900 text-sm leading-snug">{it.title}</span>
                      {it.snippet && <span className="block text-rose-500 text-xs mt-1 line-clamp-2">{it.snippet}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Refine + generate */}
        <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6 space-y-4 lg:sticky lg:top-24">
          <div className="font-bold text-rose-900 flex items-center gap-2"><Wand2 className="w-4 h-4 text-pink-500" /> Tune the voice</div>

          <div>
            <label className="text-xs font-semibold text-rose-400">Tone / style</label>
            <Input value={tone} onChange={(e) => setTone(e.target.value)} data-testid="manchi-tone-input"
              placeholder="e.g. witty, confident, student-friendly"
              className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-rose-400">Language</label>
            <Input value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="manchi-language-input"
              placeholder="English"
              className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-rose-400">Length</label>
            <div className="mt-1 grid grid-cols-3 gap-1.5">
              {LENGTHS.map((l) => (
                <button key={l} onClick={() => setLength(l)} data-testid={`manchi-length-${l.toLowerCase()}`}
                  className={`text-xs font-semibold py-2 rounded-xl border ${length === l ? "bg-pink-500 text-white border-pink-500" : "border-pink-200 text-rose-500 hover:bg-pink-50"}`}>
                  {l}
                </button>
              ))}
            </div>
            {length === "Custom" && (
              <div className="mt-2 flex items-center gap-2">
                <Input type="number" min="100" max="4000" value={customWords}
                  onChange={(e) => setCustomWords(e.target.value)} data-testid="manchi-custom-words"
                  className="rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 h-9 w-28" />
                <span className="text-xs text-rose-400">words (100–4000)</span>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-rose-400">Extra prompt (optional)</label>
            <Textarea value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="manchi-extra-input"
              placeholder="e.g. relate it to my microeconomics module; add one data point; keep it optimistic"
              rows={3} className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 resize-none" />
          </div>

          <Button onClick={generate} disabled={generating} data-testid="manchi-generate-btn"
            className="w-full h-11 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold shadow-lg shadow-pink-300/40">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {generating ? "Writing…" : selCount ? `Generate from ${selCount} stor${selCount === 1 ? "y" : "ies"}` : "Generate from top stories"}
          </Button>

          <div className="pt-4 mt-2 border-t border-pink-100">
            <div className="font-bold text-rose-900 flex items-center gap-2 mb-2 text-sm"><Link2 className="w-4 h-4 text-pink-500" /> Summarise a link</div>
            <div className="flex gap-2">
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} data-testid="manchi-link-input"
                placeholder="Paste an article URL you liked"
                className="rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 h-9 text-sm" />
              <Button onClick={summarizeLink} disabled={summarizing} data-testid="manchi-link-summarize-btn"
                className="rounded-xl bg-pink-500 hover:bg-pink-600 text-white h-9 px-3">
                {summarizing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Go"}
              </Button>
            </div>
            {linkSummary && (
              <div data-testid="manchi-link-summary" className="mt-3 rounded-2xl bg-pink-50/60 border border-pink-100 p-3">
                <div className="font-semibold text-rose-900 text-sm leading-snug">{linkSummary.title}</div>
                <div className="text-rose-700 text-xs mt-1.5 whitespace-pre-line max-h-40 overflow-y-auto">{linkSummary.summary}</div>
                {linkSummary.angle && <div className="text-pink-600 text-xs mt-2 italic">💡 {linkSummary.angle}</div>}
                <Button onClick={useLinkAsSource} data-testid="manchi-link-use-btn"
                  className="mt-3 w-full rounded-xl bg-white border border-pink-300 text-rose-600 hover:bg-pink-50 h-8 text-xs">
                  Use this as a source
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Result */}
      {article && article.status === "generating" && (
        <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-10 text-center" data-testid="manchi-article-generating">
          <Loader2 className="w-7 h-7 animate-spin text-pink-500 inline" />
          <div className="text-rose-900 font-bold mt-3">Writing your {article.length}…</div>
          <div className="text-rose-400 text-sm mt-1">Long-form pieces take a little longer — it'll appear here and in Drafts.</div>
        </div>
      )}
      {article && article.status !== "generating" && (
        <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6 space-y-4" data-testid="manchi-article-result">
          <div className="flex items-center justify-between">
            <div className="font-bold text-rose-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-pink-500" /> Your draft</div>
            <span className="text-[10px] uppercase tracking-widest bg-pink-100 text-rose-500 px-2.5 py-1 rounded-full">review before posting</span>
          </div>

          <Input value={article.title} data-testid="manchi-article-title"
            onChange={(e) => setArticle({ ...article, title: e.target.value })}
            className="rounded-xl border-pink-200 focus-visible:ring-pink-400 font-bold text-rose-900" />
          <Textarea value={article.body} data-testid="manchi-article-body"
            onChange={(e) => setArticle({ ...article, body: e.target.value })}
            rows={12} className="rounded-xl border-pink-200 focus-visible:ring-pink-400 text-rose-800 leading-relaxed" />
          <Input value={(article.hashtags || []).join(" ")} data-testid="manchi-article-hashtags"
            onChange={(e) => setArticle({ ...article, hashtags: e.target.value.split(/\s+/).filter(Boolean) })}
            className="rounded-xl border-pink-200 focus-visible:ring-pink-400 text-pink-600 font-medium" />

          {article.sources?.length > 0 && (
            <div className="text-xs text-rose-400">
              Sourced from: {article.sources.slice(0, 5).map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 mr-2 hover:text-rose-600 underline">
                  {s.source || "link"} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={() => copyAndOpen(true)} data-testid="manchi-copy-open-linkedin"
              className="rounded-2xl bg-[#0a66c2] hover:bg-[#004182] text-white font-semibold">
              <Linkedin className="w-4 h-4 mr-2" /> Copy &amp; open LinkedIn
            </Button>
            <Button onClick={() => copyAndOpen(false)} variant="outline" data-testid="manchi-copy-btn"
              className="rounded-2xl border-pink-200 text-rose-600 hover:bg-pink-50">
              <Copy className="w-4 h-4 mr-2" /> Copy text
            </Button>
            <Button onClick={saveEdits} disabled={saving} variant="outline" data-testid="manchi-save-btn"
              className="rounded-2xl border-pink-200 text-rose-600 hover:bg-pink-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save to drafts
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
